import { DateTime } from 'luxon';
import { EMPLOYMENT_STATUS_LABELS, EMPLOYMENT_TYPE_LABELS, WORK_ARRANGEMENT_LABELS, countryName as sharedCountryName } from '@workforce/shared';
import { currentLanguage, t } from './i18n';

export function fmtDate(d: string | null | undefined, format = 'dd LLL yyyy'): string {
  if (!d) return '—';
  const dt = DateTime.fromISO(d);
  return dt.isValid ? dt.toFormat(format) : d;
}

export const employmentType = (v: string) => {
  const l = EMPLOYMENT_TYPE_LABELS[v as keyof typeof EMPLOYMENT_TYPE_LABELS];
  return l ? t(l) : v;
};
export const employmentStatus = (s: string) => {
  const l = EMPLOYMENT_STATUS_LABELS[s as keyof typeof EMPLOYMENT_STATUS_LABELS];
  return l ? t(l) : s;
};
export const workArrangement = (w: string) => {
  const l = WORK_ARRANGEMENT_LABELS[w as keyof typeof WORK_ARRANGEMENT_LABELS];
  return l ? t(l) : w;
};

let arRegions: Intl.DisplayNames | null = null;
/** Country name in the current language (Arabic via Intl, falling back to the English name). */
export function countryName(code: string | null | undefined, englishName?: string): string {
  const en = englishName ?? sharedCountryName(code);
  if (!code || currentLanguage() !== 'ar') return en;
  try {
    arRegions ??= new Intl.DisplayNames(['ar'], { type: 'region' });
    return arRegions.of(code) ?? en;
  } catch {
    return en;
  }
}
export const country = (c: string | null | undefined) => countryName(c);

export function flag(code: string | null | undefined): string {
  if (!code || code.length !== 2) return '';
  return String.fromCodePoint(...[...code.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
}

/** Human description of an audit action key. */
export function describeAction(action: string): string {
  const map: Record<string, string> = {
    'auth.login': 'Signed in',
    'auth.logout': 'Signed out',
    'auth.login_failed': 'Failed sign-in',
    'auth.password_changed': 'Changed password',
    'account.created': 'Login account created',
    'account.updated': 'Login account changed',
    'account.password_reset': 'Password reset by admin',
    'employee.created': 'Employee created',
    'employee.updated': 'Employee updated',
    'employee.deactivated': 'Employee deactivated',
    'employee.reactivated': 'Employee reactivated',
    'employee.deleted': 'Employee deleted',
    'employee.work_terms_added': 'Work terms changed',
    'employee.work_terms_added_retroactive': 'Work terms changed (retroactive)',
    'employee.work_terms_removed': 'Scheduled work-terms change removed',
    'employee.settings_changed': 'Employee settings changed',
    'setting.changed': 'Company setting changed',
    'holiday_calendar.synced': 'Holidays synced from official source',
    'attendance.punch_added': 'Attendance punch added by admin',
    'attendance.punch_voided': 'Attendance punch voided',
    'attendance.imported': 'Attendance imported from machine',
    'attendance.device_created': 'Attendance device added',
    'attendance.device_updated': 'Attendance device changed',
    'attendance.device_key_rotated': 'Attendance device key replaced',
    'compensation.added': 'Salary set / changed',
    'bank_account.added': 'Bank details added',
    'bank_account.updated': 'Bank details changed',
    'bank_account.removed': 'Bank details removed',
    'compensation.removed': 'Upcoming salary change removed',
    'payroll.rate_set': 'Payroll exchange rate set',
    'payroll.adjustment_added': 'Payroll adjustment added',
    'payroll.adjustment_removed': 'Payroll adjustment removed',
    'payroll.finalized': 'Payroll finalised',
    'payroll.reopened': 'Payroll reopened',
    'productivity.notice_accepted': 'Monitoring notice accepted',
    'productivity.enrollment_code_created': 'Computer enrolment code created',
    'productivity.device_enrolled': 'Computer connected',
    'productivity.device_revoked': 'Computer disconnected',
    'productivity.rule_created': 'App/website classified',
    'productivity.rule_updated': 'App/website classification changed',
    'productivity.rule_deleted': 'App/website classification removed',
    'productivity.starter_rules_loaded': 'Starter app list loaded',
    'productivity.retention_applied': 'Old activity records deleted (retention)',
    'screenshot.viewed': 'Screenshot viewed',
    'screenshot.deleted': 'Screenshot deleted',
    'period.closed': 'Reporting period closed',
    'period.reopened': 'Reporting period reopened',
    'leave.requested': 'Leave requested',
    'leave.approved': 'Leave approved',
    'leave.auto_approved': 'Leave recorded (no approval needed)',
    'leave.rejected': 'Leave rejected',
    'leave.cancelled': 'Leave cancelled',
    'leave.balance_adjusted': 'Leave balance adjusted',
    'leave.entitlement_changed': 'Leave allowance changed',
    'leave_type.created': 'Leave type created',
    'leave_type.updated': 'Leave type changed',
    'leave_type.deleted': 'Leave type deleted',
    'leave_policy.created': 'Leave policy created',
    'leave_policy.updated': 'Leave policy changed',
    'leave_policy.deleted': 'Leave policy deleted',
  };
  return map[action] ? t(map[action]) : action.replace(/[._]/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}
